import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Send,
  CheckCircle2,
  ArrowRight,
  Navigation
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactInfo = [
  {
    icon: MapPin,
    title: 'Main Office',
    details: ['901 S Central Expy Ste 100', 'Richardson, TX 75080'],
    highlight: true,
  },
  {
    icon: Phone,
    title: 'Phone',
    details: ['(855) 831-2075'],
    highlight: true,
  },
  {
    icon: Mail,
    title: 'Email',
    details: ['info@healthchecksolutions.online'],
    highlight: true,
  },
];

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-zinc-950 min-h-screen pt-20">
      {/* Hero */}
      <section className="py-32 relative overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-[150px]" />
        
        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="overline mb-6 block">Contact</span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[0.95] mb-8 font-['Outfit'] tracking-tighter">
              Let's start a<br />
              <span className="gradient-text">conversation</span>
            </h1>
            <p className="text-lg md:text-xl text-zinc-400 leading-relaxed">
              Have questions? Ready to bring health screenings to your workplace? We're here to help.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 border-t border-white/5" data-testid="contact-info">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`card-surface rounded-2xl p-6 ${info.highlight ? 'border-emerald-500/30' : ''}`}
                data-testid={`contact-info-${index}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${
                  info.highlight 
                    ? 'bg-emerald-500/10 border border-emerald-500/20' 
                    : 'bg-zinc-800 border border-white/5'
                }`}>
                  <info.icon className={`w-5 h-5 ${info.highlight ? 'text-emerald-500' : 'text-zinc-500'}`} />
                </div>
                <h3 className="text-white font-medium mb-2 font-['Outfit']">{info.title}</h3>
                {info.details.map((detail, i) => (
                  <p key={i} className={`text-sm ${info.highlight ? 'text-zinc-400' : 'text-zinc-600'}`}>{detail}</p>
                ))}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Maps */}
      <section className="py-16" data-testid="map-section">
        <div className="container mx-auto px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-4">
              <Navigation className="w-5 h-5 text-emerald-500" />
              <h2 className="text-2xl font-bold text-white font-['Outfit']">Our Main Office</h2>
            </div>
            <p className="text-zinc-500">
              Visit us at our new Richardson, TX headquarters. We're conveniently located and ready to serve you.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="rounded-2xl overflow-hidden border border-white/10"
          >
            <iframe
              src="https://www.google.com/maps?q=901+S+Central+Expy+Ste+100,+Richardson,+TX+75080&output=embed"
              width="100%"
              height="450"
              style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(83%)' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="HealthCheck Systems Office Location"
              data-testid="google-map"
            />
          </motion.div>
          
          <div className="mt-4 flex items-center justify-between flex-wrap gap-4">
            <p className="text-zinc-600 text-sm">
              901 S Central Expy Ste 100, Richardson, TX 75080
            </p>
            <a 
              href="https://www.google.com/maps/dir//901+S+Central+Expy+Ste+100,+Richardson,+TX+75080"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-500 text-sm hover:underline flex items-center gap-2"
            >
              Get Directions <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-24 bg-zinc-900/30" data-testid="contact-form-section">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="card-surface rounded-3xl p-8 md:p-10">
                <h2 className="text-2xl font-bold text-white mb-8 font-['Outfit']">Send a Message</h2>

                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">Message Sent!</h3>
                    <p className="text-zinc-500 mb-6">We'll get back to you within 24 hours.</p>
                    <Button
                      onClick={() => setIsSubmitted(false)}
                      variant="outline"
                      className="rounded-full border-white/20 text-white hover:bg-white/5"
                      data-testid="send-another-btn"
                    >
                      Send Another Message
                    </Button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-sm text-zinc-400 mb-2 block">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="John Smith"
                          className="bg-zinc-900 border-white/10 text-white placeholder:text-zinc-600 focus:border-emerald-500 rounded-lg"
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-sm text-zinc-400 mb-2 block">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="john@company.com"
                          className="bg-zinc-900 border-white/10 text-white placeholder:text-zinc-600 focus:border-emerald-500 rounded-lg"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone" className="text-sm text-zinc-400 mb-2 block">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(555) 123-4567"
                          className="bg-zinc-900 border-white/10 text-white placeholder:text-zinc-600 focus:border-emerald-500 rounded-lg"
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="company" className="text-sm text-zinc-400 mb-2 block">Company</Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Acme Inc."
                          className="bg-zinc-900 border-white/10 text-white placeholder:text-zinc-600 focus:border-emerald-500 rounded-lg"
                          data-testid="contact-company-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-sm text-zinc-400 mb-2 block">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder="Tell us about your health screening needs..."
                        className="bg-zinc-900 border-white/10 text-white placeholder:text-zinc-600 focus:border-emerald-500 rounded-lg resize-none"
                        data-testid="contact-message-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full rounded-full py-6 bg-emerald-500 hover:bg-emerald-400 text-black font-medium transition-all duration-200"
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? 'Sending...' : (
                        <>
                          Send Message
                          <Send className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex flex-col justify-center"
            >
              <span className="overline mb-4 block">Get in Touch</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Outfit'] tracking-tighter">
                We're here to help
              </h2>
              <p className="text-zinc-400 text-lg leading-relaxed mb-8">
                Whether you have questions about our services, want to discuss healthcare provider 
                management, or schedule a consultation, our team is ready to assist you.
              </p>

              <div className="space-y-4 mb-10">
                {[
                  { icon: Clock, text: 'Response within 24 hours' },
                  { icon: CheckCircle2, text: 'Free initial consultation' },
                  { icon: CheckCircle2, text: 'Healthcare provider management services' },
                ].map((item, index) => (
                  <motion.div 
                    key={index} 
                    className="flex items-center gap-3" 
                    data-testid={`contact-feature-${index}`}
                    whileHover={{ x: 5 }}
                  >
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                      <item.icon className="w-4 h-4 text-emerald-500" />
                    </div>
                    <span className="text-zinc-300">{item.text}</span>
                  </motion.div>
                ))}
              </div>

              <div className="bg-gradient-to-br from-emerald-500/10 to-transparent rounded-2xl p-6 border border-emerald-500/20">
                <h3 className="text-white font-semibold mb-2">Ready to get started?</h3>
                <p className="text-zinc-500 text-sm mb-4">
                  Skip the form and schedule directly.
                </p>
                <Link to="/request-screening" data-testid="contact-request-link">
                  <Button className="w-full rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-medium">
                    Request Screening
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
